import { useState, forwardRef, useImperativeHandle, Ref } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { api } from '@/api/client';
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "@/locales";





// 组件属性定义
interface AddUserDialogProps {
    // 新增成功后的回调
    onSuccess?: (response:any) => void;
}

// 暴露给父组件的方法类型
interface DialogRef {
    open: () => void;
}

export const AddUserDialog = forwardRef<DialogRef, AddUserDialogProps>(
    ({ onSuccess }, ref) => {
        const { t } = useTranslation();
        
        // 表单数据类型定义
        const userFormSchema = z.object({
            // name: z.string().min(2, "姓名至少2个字符"),
            email: z.string().email(t('admin.validation.emailInvalid')),
            type: z.string().min(1, t('admin.validation.inviteTypeRequired')),
            // phone: z.string().optional(),
        });

        type UserFormValues = z.infer<typeof userFormSchema>;
        
        // 控制弹窗显示状态
        const [isOpen, setIsOpen] = useState(false);
        // 控制提交加载状态
        const [isSubmitting, setIsSubmitting] = useState(false);
        // 错误信息
        const [errorMessage, setErrorMessage] = useState("");

        // 表单配置
        const form = useForm<UserFormValues>({
            resolver: zodResolver(userFormSchema),
            defaultValues: {
                email: "",
                type: "",
            },
        });

        // 暴露open方法给父组件
        useImperativeHandle(ref, () => ({
            open: () => {
                setIsOpen(true);
                form.reset(); // 打开时重置表单
                setErrorMessage(""); // 清空错误信息
            },
        }));

        // 处理表单提交
        const onSubmit = async (data: UserFormValues) => {
            try {
                setIsSubmitting(true);
                setErrorMessage("");
                console.log(data)
                // return false

                // 调用发起邀请
                const response:any = await api.post('/v1/admin/team/user/invite', {},{
                    params:{
                        ...data,
                    }
                });

                if (response.type) {
                    // 接口成功 - 关闭弹窗并触发回调
                    setIsOpen(false);
                    onSuccess?.(response);
                } else {
                    // 接口返回错误信息
                    setErrorMessage(response.message || t('admin.addUser.addFailed'));
                }
            } catch (error) {
                // 捕获网络等异常错误
                // console.log(error.data.msg)
                setErrorMessage(error.data.msg || t('common.networkError'));
            } finally {
                setIsSubmitting(false);
            }
        };

        return (
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t('admin.addUser.inviteMember')}</DialogTitle>
                        <DialogDescription>
                            {t('admin.addUser.fillMemberInfo')}
                        </DialogDescription>
                    </DialogHeader>

                    {/* 错误提示 */}
                    {errorMessage && (
                        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
                            {errorMessage}
                        </div>
                    )}

                    {/* 用户表单 */}
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('admin.email')} *</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t('admin.pleaseEnterEmail')} type="email" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="type"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('admin.addUser.inviteType')} *</FormLabel>
                                        <Select
                                            onValueChange={field.onChange}
                                            defaultValue=""  // 显式设置默认值为空，触发校验
                                            value={field.value}  // 绑定表单字段值
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t('admin.addUser.selectType')} />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="EMAIL">{t('admin.email')}</SelectItem>
                                                <SelectItem value="LINK">{t('admin.addUser.link')}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />


                            <DialogFooter>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsOpen(false)}
                                    disabled={isSubmitting}
                                >
                                    {t('common.cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? t('common.submitting') : t('common.confirm')}
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
        );
    }
);

AddUserDialog.displayName = "AddUserDialog";
